import React, { createContext, useCallback, useContext, useMemo } from "react";
import { deleteVal, getVal, setVal } from "oolib";
import { useQueryClient } from "react-query";
import { segrigateDocs } from "@okf/ootils";

import { ANNO_GROUP_DISPLAY } from "@/components/DataFieldInputSelector/CascadeFieldPicker/utils/buildCascadeTree";

import { _extractOptionsFromContentTypes } from "../../WidgetBarChart/utils/_extractOptionsFromContentTypes";
import { useTopicClusteringDimensionOptions } from "../../WidgetBarChart/utils/useTopicClusteringDimensionOptions";
import { useArithmeticExpression } from "../../utils/ArithmeticExpressionInput/useArithmeticExpression";
import { WIDGET_COMPATIBLE_COMPS } from "../../utils/WIDGET_COMPATIBLE_COMPS";
import { DATA_KEYS } from "./DATA_KEYS";
import { getWidgetContentTypes, pruneStaleWidgetFilters } from "../../utils/getWidgetContentTypes";
import { readValueSelectionState, isNothingPicked, VALUE_SELECTION_KEYS } from "../utils/valueSelection";
import { useGetFieldValueCounts } from "../queries/useGetFieldValueCounts";


// --- Context ---

/** Prefix for the synthetic "count this dataset's docs" option emitted by a
 *  selectable dataset folder. Only needs to be unique against real field values,
 *  so the picker can highlight the right folder when you reopen it. */
const DOC_COUNT_VALUE_PREFIX = "__DOC_COUNT__";

/** The collection an annotation-based field (Topics) measures. Not a tpl —
 *  the BE only accepts a topicClusteringNode dimension against this one. */
const ANNO_CONTENT_TYPE = "annotations";

const MetricCardConfigContext = createContext(null);

export const useMetricCardConfig = () => {
  const ctx = useContext(MetricCardConfigContext);
  if (!ctx) throw new Error("useMetricCardConfig must be used within MetricCardConfigProvider");
  return ctx;
};


// --- Provider ---

export const MetricCardConfigProvider = ({ thisWidgetConfig, setConfig, underEditWidgetIdx, children }) => {

  const queryClient = useQueryClient();
  const allTpls = queryClient.getQueryData("allTpls");

  /**
   * updateWidget - shared onChange boilerplate.
   * Takes an updater fn that receives a deep-cloned widget config
   * and should return the modified widget config.
   */
  const updateWidget = useCallback((updaterFn) => {
    setConfig((prev) => {
      let widgetConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      const widgetType = widgetConfig?.comp || 'WidgetMetricCard';
      const prevContentTypes = getWidgetContentTypes(widgetConfig, widgetType);
      widgetConfig = updaterFn(widgetConfig);
      // A write that changes which datasets the widget reads drops the widget
      // filters that belonged to a dataset it left (see pruneStaleWidgetFilters).
      widgetConfig = pruneStaleWidgetFilters(widgetConfig, prevContentTypes, widgetType);
      const fullConfig = JSON.parse(JSON.stringify(prev));
      fullConfig[underEditWidgetIdx] = widgetConfig;
      return fullConfig;
    });
  }, [setConfig, underEditWidgetIdx]);

  /**
   * clearKeys - helper to deleteVal on multiple keys at once.
   * forEach is synchronous — each iteration chains on the previous result.
   * Used inside updateWidget updater fns:
   *   updateWidget(w => clearKeys(w, [dataKeys.field, dataKeys.statFunction]))
   */
  const clearKeys = useCallback((widget, keys) => {
    let result = widget;
    keys.forEach(key => {
      result = deleteVal(result, key);
    });
    return result;
  }, []);

  // --- Measure mode ---
  const measureMode = getVal(thisWidgetConfig, DATA_KEYS.measureMode) || 'fieldStat';

  /**
   * buildDocCountOption - the option a DATASET FOLDER emits when its row body is
   * picked in the cascade picker, i.e. "count the documents in this dataset".
   * Carries display/icon metadata for the collapsed card, and a stable unique
   * `value` so the picker can mark the folder as selected.
   */
  const buildDocCountOption = useCallback((contentType, display) => ({
    __docCount: true,
    value: `${DOC_COUNT_VALUE_PREFIX}${contentType}`,
    display,
    contentType,
    iconName: "Files",
    dataType: "Content Type"
  }), []);

  /**
   * allFieldOptions - all compatible field options from all publishing content types,
   * grouped by content type name.
   *
   * Uses _extractOptionsFromContentTypes (shared with BarChart) to pull fields from
   * all publishing templates, then filters to only the comps compatible with MetricCard
   * (from WIDGET_COMPATIBLE_COMPS), and groups by content type using segrigateDocs.
   *
   * Shape: [{ display: "Content Type Name", options: [{ display, value, valuePath, comp, contentType }] }]
   *
   * Shared via context because both DataSection (field picker) and the arithmetic hook
   * (for filtering NumberInput options for fields B, C, D) consume it.
   *
   * Topics ride in front of the dataset groups under ANNO_GROUP_DISPLAY — the
   * group name buildCascadeTree keys its "Annotations" section off. The picker
   * folds them into its Topics folder on its own (folder body = "every topic",
   * chevron = one leaf per topic, which measures that topic's sub-topics), the
   * same contract as the bar chart's dimension slot. Empty on non-split
   * tenants, where the hook returns [] and no section renders.
   */
  const topicClusteringOptions = useTopicClusteringDimensionOptions();

  const allFieldOptions = useMemo(() => {
    if (!allTpls || !Array.isArray(allTpls)) return [];

    const publishingContentTypes = allTpls
      .filter(tpl => tpl.general?.segment === 'publishing')
      .map(d => d.kp_content_type);

    const compatibleComps = WIDGET_COMPATIBLE_COMPS['WidgetMetricCard'] || [];

    const allOptions = _extractOptionsFromContentTypes({
      contentTypes: publishingContentTypes,
      allTpls,
      showNumberInputsInOptions: true
    });

    const compatibleOptions = allOptions.filter(opt => compatibleComps.includes(opt.comp));

    // Group by content type for the drill-down lightbox picker
    const grouped = segrigateDocs(compatibleOptions, 'contentType');
    const datasetGroups = Object.keys(grouped).map(ct => {
      const display = allTpls.find(tpl => tpl.kp_content_type === ct)?.general?.content?.title || ct;
      return {
        display,
        value: ct,
        options: grouped[ct],
        // Makes the dataset folder itself selectable: buildCascadeTree copies this
        // onto the folder row, so the row body picks the dataset (document count)
        // while the chevron still drills into its fields (field statistic).
        selectOption: buildDocCountOption(ct, display)
      };
    });

    return [
      ...(topicClusteringOptions.length > 0
        ? [{ display: ANNO_GROUP_DISPLAY, value: ANNO_CONTENT_TYPE, options: topicClusteringOptions }]
        : []),
      ...datasetGroups,
    ];
  }, [allTpls, buildDocCountOption, topicClusteringOptions]);

  /**
   * handleFieldChange - cascade handler for field selection changes.
   * Called by the arithmetic hook's onChange when field A changes or an expression is built.
   *
   * Handles:
   * - Setting the field value in widget config
   * - Deriving content type from the selected field
   * - For numeric fields (NumberInput): clearing categorical values, defaulting statFunction to 'avg', setting title
   * - For categorical fields: clearing statFunction, clearing selected values, clearing title
   * - For null (field cleared): clearing all downstream keys
   *
   * Lives in context because the arithmetic hook fires it from both
   * DataSection (field A picker) and ConfigureSection (aggregate changes).
   */
  const handleFieldChange = useCallback((newFieldValue) => {
    updateWidget(w => {
      // Null = field cleared, reset everything downstream
      if (newFieldValue == null) {
        w = deleteVal(w, DATA_KEYS.field);
        w = deleteVal(w, DATA_KEYS.contentType);
        w = deleteVal(w, DATA_KEYS.statFunction);
        w = deleteVal(w, DATA_KEYS.selectedFieldValue);
        w = deleteVal(w, DATA_KEYS.selectedFieldValueNames);
        w = deleteVal(w, DATA_KEYS.categoricalStatFunction);
        w = deleteVal(w, DATA_KEYS.displayMode);
        w = deleteVal(w, DATA_KEYS.field1);
        w = deleteVal(w, DATA_KEYS.measureMode);
        w = deleteVal(w, 'props.hasCustomTitle');
        return w;
      }

      // Picking a field IS the choice of fieldStat, and it may be replacing a
      // docCount pick, so the mode has to be stamped rather than assumed.
      w = setVal(w, DATA_KEYS.measureMode, 'fieldStat');
      w = setVal(w, DATA_KEYS.field, newFieldValue);

      // Derive content type from selected field. An annotation-based field
      // (Topics) has no tpl contentType — it measures the annotations
      // collection, which is also the only collection the BE accepts a
      // topicClusteringNode dimension against.
      if (newFieldValue.contentType) {
        w = setVal(w, DATA_KEYS.contentType, newFieldValue.contentType);
      } else if (newFieldValue.isAnno) {
        w = setVal(w, DATA_KEYS.contentType, ANNO_CONTENT_TYPE);
      }

      const hasCustomTitle = getVal(w, 'props.hasCustomTitle');
      const isNumeric = newFieldValue.comp === 'NumberInput';

      if (isNumeric) {
        // Clear categorical-specific keys
        w = deleteVal(w, DATA_KEYS.selectedFieldValue);
        w = deleteVal(w, DATA_KEYS.selectedFieldValueNames);
        w = deleteVal(w, DATA_KEYS.categoricalStatFunction);
        w = deleteVal(w, DATA_KEYS.displayMode);
        // Default stat function to 'avg' if none set
        const currentStat = getVal(w, DATA_KEYS.statFunction);
        if (!currentStat) {
          w = setVal(w, DATA_KEYS.statFunction, 'avg');
        }
        if (!hasCustomTitle) {
          w = setVal(w, DATA_KEYS.field1, newFieldValue.display || '');
        }
      } else {
        // Clear numeric-specific keys
        w = deleteVal(w, DATA_KEYS.statFunction);
        w = deleteVal(w, DATA_KEYS.selectedFieldValue);
        w = deleteVal(w, DATA_KEYS.selectedFieldValueNames);
        w = deleteVal(w, DATA_KEYS.categoricalStatFunction);
        if (!hasCustomTitle) {
          w = deleteVal(w, DATA_KEYS.field1);
        }
      }

      return w;
    });
  }, [updateWidget]);

  /**
   * handleDocCountSelect - a DATASET FOLDER body was picked in the cascade picker:
   * switch to docCount for that content type and drop the field-stat keys, which
   * mean nothing when counting documents.
   *
   * Deliberately ONE updateWidget call. setConfig is routed through the collab
   * layer's applyConfig, so two updates in the same tick both read the same base
   * and the last one wins — splitting the mode switch and the content-type write
   * into two calls silently loses the mode switch.
   */
  const handleDocCountSelect = useCallback((option) => {
    updateWidget(w => {
      w = setVal(w, DATA_KEYS.measureMode, 'docCount');
      w = setVal(w, DATA_KEYS.contentType, option.contentType);
      w = deleteVal(w, DATA_KEYS.field);
      w = deleteVal(w, DATA_KEYS.statFunction);
      w = deleteVal(w, DATA_KEYS.selectedFieldValue);
      w = deleteVal(w, DATA_KEYS.selectedFieldValueNames);
      w = deleteVal(w, DATA_KEYS.categoricalStatFunction);
      w = deleteVal(w, DATA_KEYS.displayMode);
      if (!getVal(w, 'props.hasCustomTitle')) {
        w = setVal(w, DATA_KEYS.field1, option.display);
      }
      return w;
    });
  }, [updateWidget]);

  /**
   * handleClearData - empties the data slot from either measure mode, back to the
   * pristine "nothing picked yet" state (measureMode deleted => fieldStat default).
   */
  const handleClearData = useCallback(() => {
    updateWidget(w => {
      w = deleteVal(w, DATA_KEYS.measureMode);
      w = deleteVal(w, DATA_KEYS.field);
      w = deleteVal(w, DATA_KEYS.contentType);
      w = deleteVal(w, DATA_KEYS.statFunction);
      w = deleteVal(w, DATA_KEYS.selectedFieldValue);
      w = deleteVal(w, DATA_KEYS.selectedFieldValueNames);
      w = deleteVal(w, DATA_KEYS.categoricalStatFunction);
      w = deleteVal(w, DATA_KEYS.displayMode);
      if (!getVal(w, 'props.hasCustomTitle')) {
        w = deleteVal(w, DATA_KEYS.field1);
      }
      return w;
    });
  }, [updateWidget]);

  /**
   * arithmeticState - all state + handlers from the useArithmeticExpression hook.
   * Shared between DataSection (field rows for B, C, D...) and ConfigureSection
   * (aggregate dropdown + custom expression input).
   *
   * The hook manages:
   * - dataFields (A, B, C... field selections)
   * - aggregateType, customExpression, validationError
   * - handlers for adding/removing/changing fields and aggregate type
   * - computed: numberInputOptions, canAddFields, aggregateOptions, hasMultipleFields
   *
   * handleFieldChange is passed as onChange — so when the hook emits a value
   * (single field or expression), the cascade logic above handles setting
   * defaults and clearing stale values.
   */
  const selectedField = getVal(thisWidgetConfig, DATA_KEYS.field);

  const arithmeticState = useArithmeticExpression({
    allOptions: allFieldOptions,
    value: selectedField,
    onChange: handleFieldChange,
    disabled: false
  });

  /**
   * docCountValue - what the (now single) data card shows in docCount mode.
   * Prefers the group's own selectOption so the picker highlights the matching
   * folder by equal `value`; falls back to a rebuilt option while allTpls is
   * still loading, or if the saved content type has no compatible fields.
   */
  const docCountValue = useMemo(() => {
    if (measureMode !== 'docCount') return null;
    const contentType = getVal(thisWidgetConfig, DATA_KEYS.contentType);
    if (!contentType) return null;
    const group = allFieldOptions.find(g => g.value === contentType);
    return group?.selectOption || buildDocCountOption(contentType, contentType);
  }, [measureMode, thisWidgetConfig, allFieldOptions, buildDocCountOption]);

  // --- Categorical value counts ---
  // Owned here rather than in DataSection because the value picker AND the
  // stat-function gate below both need the option universe: a manual selection
  // with no stored key means "every value", which only these counts can size.
  //
  // The value picker is the widget's level-one choice ("which values of this
  // field"); the widget's filters are level two and narrow the RESULT, never the
  // choice. So the options are fetched unfiltered — a filter on Country=India
  // must not make Nakuru disappear from the State picker. The card's own query
  // (../index.js) still applies the filters when it computes the number.
  const isNumberInput = selectedField?.comp === 'NumberInput';
  const { data: fieldValueCounts, status: fieldValueCountsStatus } = useGetFieldValueCounts({
    contentType: getVal(thisWidgetConfig, DATA_KEYS.contentType),
    field: selectedField,
    activeFilters: [],
    // Keyed by the widget id like the card's own query (../index.js), so an
    // unfiltered card shares one request with it rather than firing a second.
    vizCardId: thisWidgetConfig?.id,
    queryOptions: {
      enabled: measureMode === 'fieldStat' && !isNumberInput && !!selectedField && !!getVal(thisWidgetConfig, DATA_KEYS.contentType)
    }
  });

  const fieldValueOptions = useMemo(() => {
    if (!Array.isArray(fieldValueCounts)) return [];
    return fieldValueCounts.map(item => ({
      display: `${item.display}`,
      value: item.value,
      count: item.count,
      percentage: item.percentage,
      displayName: item.display
    }));
  }, [fieldValueCounts]);

  // --- Visibility flags for animated sections ---
  const selectedFieldValues = getVal(thisWidgetConfig, DATA_KEYS.selectedFieldValue);
  const valueSelection = readValueSelectionState(thisWidgetConfig);
  // Dynamic mode aggregates N values; manual aggregates the stored list — and
  // every value is counted as the whole option set, which covers BOTH ways of
  // spelling it: the absent key, and a legacy [] the picker never wrote (only a
  // [] with a recorded manual mode is a real "nothing picked" — isNothingPicked).
  // Reading a legacy [] as 0 hid the Configure section, so the stat function the
  // card was actually aggregating with became invisible and unchangeable.
  const storedSelectionMode = getVal(thisWidgetConfig, VALUE_SELECTION_KEYS.modeKey);
  const selectedValuesCount = valueSelection.mode === 'dynamic'
    ? valueSelection.n
    : Array.isArray(selectedFieldValues)
      ? (isNothingPicked(selectedFieldValues, storedSelectionMode)
          ? 0
          : (selectedFieldValues.length || fieldValueOptions.length))
      : (selectedFieldValues ? 1 : fieldValueOptions.length);
  const showConfigureSection = measureMode !== 'docCount' && !!selectedField && (isNumberInput || selectedValuesCount > 1);
  const showDisplaySection = measureMode !== 'docCount' && !!selectedField && !isNumberInput;

  const value = useMemo(() => ({
    widgetConfig: thisWidgetConfig,
    updateWidget,
    clearKeys,
    dataKeys: DATA_KEYS,
    measureMode,
    allFieldOptions,
    handleFieldChange,
    handleDocCountSelect,
    handleClearData,
    docCountValue,
    arithmeticState,
    showConfigureSection,
    showDisplaySection,
    effectiveSelectedCount: selectedValuesCount,
    fieldValueOptions,
    fieldValueCountsStatus,
    // expose raw setConfig + index for edge cases (e.g. WidgetFilters)
    setConfig,
    underEditWidgetIdx
  }), [thisWidgetConfig, updateWidget, clearKeys, measureMode, allFieldOptions, handleFieldChange, handleDocCountSelect, handleClearData, docCountValue, arithmeticState, showConfigureSection, showDisplaySection, selectedValuesCount, fieldValueOptions, fieldValueCountsStatus, setConfig, underEditWidgetIdx]);

  return (
    <MetricCardConfigContext.Provider value={value}>
      {children}
    </MetricCardConfigContext.Provider>
  );
};
